import React, { useMemo } from 'react';
import { Alert, Descriptions, Empty, Modal, Progress, Space, Table, Tag, Timeline, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGenerationRun } from '../../hooks/useGenerationRuns';
import type { GenerationRun, RunLogEntry } from '../../types';
import ToolCallCounter from './ToolCallCounter';
import {
  formatCurrency,
  formatDuration,
  getPhaseLabel,
  getRunStatusColor,
} from './providerMeta';

const { Text } = Typography;

interface RunDetailModalProps {
  runId: string | null;
  open: boolean;
  fallbackRun?: GenerationRun | null;
  onClose: () => void;
}

const RunDetailModal: React.FC<RunDetailModalProps> = ({ runId, open, fallbackRun, onClose }) => {
  const { data: runFromApi, isLoading, error } = useGenerationRun(open ? runId : null);
  const run = runFromApi || fallbackRun || null;

  const logColumns = useMemo<ColumnsType<RunLogEntry>>(
    () => [
      {
        title: 'Timestamp',
        dataIndex: 'timestamp',
        key: 'timestamp',
        render: (value: string | undefined) => (value ? new Date(value).toLocaleString() : '—'),
      },
      {
        title: 'Provider',
        dataIndex: 'provider',
        key: 'provider',
        render: (value: string) => <Tag>{value}</Tag>,
      },
      {
        title: 'Phase',
        dataIndex: 'phase',
        key: 'phase',
        render: (value: string | undefined) => getPhaseLabel(value),
      },
      {
        title: 'Model',
        dataIndex: 'model',
        key: 'model',
        render: (value: string | undefined) => value || '—',
      },
      {
        title: 'Duration',
        dataIndex: 'duration_ms',
        key: 'duration_ms',
        align: 'right',
        render: (value: number | undefined) => value ? formatDuration(value / 1000) : '—',
      },
      {
        title: 'Cost',
        dataIndex: 'cost_usd',
        key: 'cost_usd',
        align: 'right',
        render: (value: number | undefined) => formatCurrency(value),
      },
    ],
    []
  );

  return (
    <Modal
      title={run ? `Run ${run.run_number ? `#${run.run_number}` : `#${run.id.slice(0, 8)}`} Breakdown` : 'Run Breakdown'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={960}
      destroyOnHidden
    >
      {error ? <Alert type="warning" showIcon message="Detailed run API is not available yet." /> : null}

      {!run ? (
        <Empty description="No run selected" />
      ) : (
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Title">{run.title || run.keyword || 'Untitled generation'}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getRunStatusColor(run.status)}>{run.status.toUpperCase()}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Started">{run.started_at ? new Date(run.started_at).toLocaleString() : '—'}</Descriptions.Item>
            <Descriptions.Item label="Completed">{run.completed_at ? new Date(run.completed_at).toLocaleString() : '—'}</Descriptions.Item>
            <Descriptions.Item label="Total Cost">{formatCurrency(run.total_cost_usd)}</Descriptions.Item>
            <Descriptions.Item label="Duration">{formatDuration(run.duration_seconds)}</Descriptions.Item>
          </Descriptions>

          {run.status === 'running' ? (
            <div>
              <Progress percent={run.progress_percentage || 0} status="active" />
              <Text type="secondary">Current phase: {getPhaseLabel(run.current_phase)}</Text>
            </div>
          ) : null}

          <div>
            <Text strong>Tool call counter</Text>
            <div style={{ marginTop: 8 }}>
              <ToolCallCounter toolCalls={run.tool_calls} />
            </div>
          </div>

          <div>
            <Text strong>Phase breakdown</Text>
            <Timeline
              style={{ marginTop: 16 }}
              items={(run.phase_breakdown || []).map((phase) => ({
                color: getRunStatusColor(phase.status || 'pending'),
                children: (
                  <Space direction="vertical" size={2}>
                    <Text strong>{phase.label || getPhaseLabel(phase.phase)}</Text>
                    <Text type="secondary">
                      {formatCurrency(phase.cost_usd)} · {formatDuration(phase.duration_seconds)}
                      {typeof phase.tool_calls === 'number' ? ` · ${phase.tool_calls} calls` : ''}
                    </Text>
                  </Space>
                ),
              }))}
            />
            {!(run.phase_breakdown || []).length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No phase timing yet" /> : null}
          </div>

          <div>
            <Text strong>Usage log entries</Text>
            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>Some calls run in parallel — durations don't sum to total</Text>
            <Table<RunLogEntry>
              style={{ marginTop: 12 }}
              rowKey="id"
              size="small"
              loading={isLoading}
              columns={logColumns}
              dataSource={run.usage_logs || []}
              pagination={{ pageSize: 5, hideOnSinglePage: true }}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No run log entries" /> }}
            />
          </div>

          {run.error ? <Alert type="error" showIcon message={run.error} /> : null}
        </Space>
      )}
    </Modal>
  );
};

export default RunDetailModal;
